import { useState, useEffect } from "react";
import {
  FaArrowLeft,
  FaPrint,
  FaUser,
  FaPhone,
  FaWifi,
  FaHashtag,
  FaCircleCheck,
  FaCircleXmark,
  FaCircleInfo,
  FaMoneyBillWave,
  FaXmark,
  FaCreditCard,
  FaBuilding,
} from "react-icons/fa6";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../../components/Header";
import { formatRupiah } from "../../../../../utilities/formatRupiah";
import { FaMapMarkerAlt, FaExternalLinkAlt, FaCalendarAlt } from "react-icons/fa";
import { useHookInvoiceDetail } from "../../../../../hooks/useHookInvoiceDetail";
import { InvoiceTransactionService } from "../../../../../services/InvoiceTransactionService";
import { SelectPaymentMethod } from "../../../../../components/forms/selects/SelectPaymentMethod";
import { InputDefault } from "../../../../../components/forms/InputDefault";

/* ============================================
   Sub-Komponen: SectionCard
   ============================================ */
const SectionCard = ({ title, icon: Icon, children }) => (
  <article
    className="rounded-2xl p-5 sm:p-6 transition-all duration-300"
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
      backdropFilter: "blur(12px)",
    }}
  >
    <div className="flex items-center gap-3 mb-5">
      {Icon && (
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{
            background: "rgba(0, 180, 216, 0.1)",
            border: "1px solid rgba(0, 180, 216, 0.2)",
          }}
        >
          <Icon className="text-ocean-400 text-sm" />
        </div>
      )}
      <h3 className="font-display text-base sm:text-lg font-bold text-gray-100">
        {title}
      </h3>
    </div>
    {children}
  </article>
);

/* ============================================
   Sub-Komponen: StatusBadge
   ============================================ */
const StatusBadge = ({ status }) => {
  const isPaid = status === "lunas" || status === "paid";
  const isUnpaid = status === "belum_bayar" || status === "unpaid" || status === "belum dibayar";

  let styleClass = "bg-slate-500/12 text-slate-400 border-slate-500/20";
  let Icon = FaCircleInfo;

  if (isPaid) {
    styleClass = "bg-emerald-500/12 text-emerald-400 border-emerald-500/20";
    Icon = FaCircleCheck;
  } else if (isUnpaid) {
    styleClass = "bg-red-500/12 text-red-400 border-red-500/20";
    Icon = FaCircleXmark;
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${styleClass}`}
    >
      <Icon className="text-[10px]" />
      {isPaid ? "Lunas" : isUnpaid ? "Belum Bayar" : status}
    </span>
  );
};

/* ============================================
   Halaman Utama: InvoiceDetail
   ============================================ */
export const InvoiceGroupDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showSidebar, setShowSidebar] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, loading: isLoading, error, refreshData } = useHookInvoiceDetail({
    id: id,
  });

  const [paymentForm, setPaymentForm] = useState({
    nominal: 0,
    payment_method_id: "", // Menggunakan ID sesuai validasi backend Laravel
    bank_account_id: "",
  });

  useEffect(() => {
    if (data) {
      setPaymentForm((prev) => ({
        ...prev,
        nominal: data?.total_nominal ?? 0,
      }));
    }
  }, [data]);

  const handlePrint = () => {
    window.print();
  };

  // Fungsi Kirim Data ke Laravel Backend
  const handleSubmitPayment = async () => {
    if (!paymentForm.payment_method_id) {
      alert("Silakan pilih metode pembayaran terlebih dahulu!");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        pppoe_user_id: id, // ID user dari URL params
        payment_method_id: paymentForm.payment_method_id,
        bank_account_id: paymentForm.bank_account_id || null, // Opsional jika cash
        nominal: Number(paymentForm.nominal),
      };

      const response = await InvoiceTransactionService.create({ data: payload });

      if (response.data.status === "success") {
        alert("Pembayaran berhasil disimpan!");
        setShowPaymentModal(false);
        refreshData(); // Refresh data halaman detail invoice agar statusnya terupdate real-time
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Terjadi kesalahan saat memproses transaksi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !data) {
    return (
      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <div className="p-6 flex items-center justify-center h-screen">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-10 h-10 rounded-full border-4 border-slate-600 border-t-ocean-400 animate-spin" />
            <p className="text-slate-400">Memuat detail invoice...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>Detail Invoice {data.id_pel || 'Pelanggan'} - RADIUS TREND</title>
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
            >
              <FaArrowLeft />
            </button>
            <div className="flex flex-col">
              <h2 className="text-lg md:text-xl font-display font-bold text-gray-100 tracking-tight">
                Detail Invoice
              </h2>
              <p className="text-xs text-slate-500 hidden md:block mt-0.5 font-mono">
                {data.id_pel}
              </p>
            </div>
          </div>

          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all duration-200 print:hidden"
            style={{
              background: "linear-gradient(135deg, #0077B6, #00B4D8)",
              boxShadow: "0 4px 15px rgba(0, 180, 216, 0.2)",
            }}
          >
            <FaPrint className="text-sm" />
            <span className="hidden sm:inline">Cetak Invoice</span>
          </button>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-6 print:bg-white print:text-black">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Data Pelanggan */}
            <div className="lg:col-span-2">
              <SectionCard title="Informasi Pelanggan" icon={FaUser}>
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6">
                  <div className="space-y-3 flex-1">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Nama Pelanggan</p>
                      <p className="text-slate-200 font-semibold text-lg">{data?.name}</p>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm text-slate-400">
                      <div className="flex items-center gap-2">
                        <FaPhone className="text-ocean-400" />
                        <span>{data.phone_number || "-"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FaWifi className="text-ocean-400" />
                        <span>{data.profile || "-"}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-slate-400">
                      <FaMapMarkerAlt className="text-ocean-400 mt-0.5 flex-shrink-0" />
                      <span>{data?.address}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate(`/dashboard/pppoe/user/detail/${id}`)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold text-cyan-400 border border-cyan-500/20 bg-cyan-500/5 hover:bg-cyan-500/10 transition-all flex-shrink-0 print:hidden"
                  >
                    Lihat Profil Pelanggan
                    <FaExternalLinkAlt className="text-[10px]" />
                  </button>
                </div>
              </SectionCard>
            </div>

            {/* Status & Total Summary */}
            <div className="lg:col-span-1">
              <div className="rounded-2xl p-5 sm:p-6 h-full flex flex-col justify-center" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                <div className="text-center mb-5">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Status Pembayaran</p>
                  <div className="flex justify-center">
                    <StatusBadge status={data?.status} />
                  </div>
                </div>
                <div className="text-center pt-5" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Total Tagihan Gabungan</p>
                  <p className="text-3xl font-extrabold text-cyan-400 tracking-tight">
                    Rp {formatRupiah(data?.total_nominal ?? 0)}
                  </p>
                </div>
                {data?.status !== "lunas" && (
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all duration-200 mt-3"
                    style={{
                      background: "linear-gradient(135deg, #059669, #10B981)",
                      boxShadow: "0 4px 15px rgba(16, 185, 129, 0.2)",
                    }}
                  >
                    <FaMoneyBillWave className="text-sm" />
                    <span>Bayar Tagihan</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Rincian Tagihan */}
          <SectionCard title="Rincian Tagihan Periode" icon={FaCalendarAlt}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700/50">
                    <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">No</th>
                    <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Kode Invoice</th>
                    <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Periode</th>
                    <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Jatuh Tempo</th>
                    <th className="pb-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Nominal</th>
                    <th className="pb-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-32">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/30">
                  {data?.invoices?.map((inv, index) => (
                    <tr key={inv.id} className="group">
                      <td className="py-4 text-slate-500 font-mono text-xs">{index + 1}</td>
                      <td className="py-4 text-slate-400 text-xs">{inv.invoice_number}</td>
                      <td className="py-4">
                        <div className="flex items-center gap-2 text-slate-200 font-medium">
                          <FaHashtag className="text-xs text-slate-600 group-hover:text-cyan-500 transition-colors" />
                          {inv.periode}
                        </div>
                      </td>
                      <td className="py-4 text-slate-400 text-xs">{inv.due_date}</td>
                      <td className="py-4 text-right font-bold text-cyan-400">
                        Rp {formatRupiah(inv?.nominal ?? 0)}
                      </td>
                      <td className="py-4 text-center">
                        <div className="flex justify-center">
                          <StatusBadge status={inv.status} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-600/50">
                    <td colSpan="4" className="pt-4 text-right text-sm font-bold text-slate-300 uppercase tracking-wider">
                      Total Gabungan
                    </td>
                    <td className="pt-4 text-right text-lg font-extrabold text-cyan-400">
                      Rp {formatRupiah(data?.total_nominal ?? 0)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </SectionCard>
        </main>

        {/* ===== PAYMENT MODAL ===== */}
        {showPaymentModal && (
          <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !isSubmitting && setShowPaymentModal(false)} />

            <div
              className="relative w-full max-w-lg rounded-3xl overflow-visible"
              style={{
                background: "rgba(15, 23, 42, 0.95)",
                border: "1px solid rgba(51, 65, 85, 0.5)",
                backdropFilter: "blur(16px)",
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-slate-700/50">
                <div>
                  <h3 className="text-lg font-bold text-white">Input Pembayaran</h3>
                  <p className="text-xs text-slate-400 mt-1">Proses pembayaran invoice pelanggan</p>
                </div>
                <button
                  disabled={isSubmitting}
                  onClick={() => setShowPaymentModal(false)}
                  className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition disabled:opacity-50"
                >
                  <FaXmark />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-5">
                <div className="rounded-2xl p-5 text-center" style={{ background: "rgba(0, 180, 216, 0.06)", border: "1px solid rgba(0, 180, 216, 0.15)" }}>
                  <h2 className="text-3xl font-extrabold text-cyan-400">Rp {formatRupiah(data?.total_nominal ?? 0)}</h2>
                  <p className="text-xs uppercase tracking-wider text-slate-400 mt-2">Total Tagihan</p>
                </div>

                {/* Nominal */}
                <InputDefault
                  label="Nominal Pembayaran"
                  value={paymentForm.nominal}
                  onChange={(e) => setPaymentForm({ ...paymentForm, nominal: e.target.value })}
                  placeholder="Masukkan nominal pembayaran"
                  disabled={isSubmitting}
                />
                {/* <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Nominal Pembayaran</label>
                  <input
                    type="number"
                    disabled={isSubmitting}
                    value={paymentForm.nominal}
                    onChange={(e) => setPaymentForm({ ...paymentForm, nominal: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-slate-200 focus:outline-none focus:border-cyan-500 disabled:opacity-50"
                    placeholder="Masukkan nominal pembayaran"
                  />
                </div> */}

                {/* Metode Pembayaran - Menggunakan ID (Isi value dengan UUID data master kamu) */}
                <SelectPaymentMethod
                  value={paymentForm.payment_method_id}
                  setValue={(val) => setPaymentForm({ ...paymentForm, payment_method_id: val })}
                  label={"Metode Pembayaran"}
                />

                {/* Akun Bank */}
                {/* <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Akun Bank (Opsional)</label>
                  <div className="relative">
                    <FaBuilding className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                    <select
                      disabled={isSubmitting}
                      value={paymentForm.bank_account_id} 
                      onChange={(e) => setPaymentForm({ ...paymentForm, bank_account_id: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-11 pr-4 py-3 text-slate-200 focus:outline-none focus:border-cyan-500 disabled:opacity-50"
                    >
                      <option value="">Pilih akun bank</option>
                      <option value="uuid-bca-mu">BCA - 123456789</option>
                      <option value="uuid-bri-mu">BRI - 987654321</option>
                    </select>
                  </div>
                </div> */}
              </div>

              {/* Footer */}
              <div className="px-6 py-5 border-t border-slate-700/50 flex items-center justify-end gap-3">
                <button
                  disabled={isSubmitting}
                  onClick={() => setShowPaymentModal(false)}
                  className="px-5 py-2.5 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 transition disabled:opacity-50"
                >
                  Batal
                </button>

                <button
                  disabled={isSubmitting}
                  onClick={handleSubmitPayment}
                  className="px-5 py-2.5 rounded-xl font-semibold text-white transition flex items-center gap-2 disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, #0077B6, #00B4D8)" }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-slate-300 border-t-white rounded-full animate-spin" />
                      Memproses...
                    </>
                  ) : (
                    "Simpan Pembayaran"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </DashboardLayout>
    </>
  );
};